import { FormEvent, useState, useContext } from "react";
import { apiAccountLoginPost } from "../api/account/login/post";
import { Box, Button, CircularProgress, Container, DialogActions, DialogContent, DialogContentText, DialogTitle, Link as MuiLink, Stack, TextField, Typography } from "@mui/material";
import logoWhite from "../assets/logo-white.png";
import { ModalContext } from "../providers/ModalProvider";
import { CloseableModal } from "../components/common/CloseableModal";

const COLORS = {
  yellow: '#EBE32D',
  white: '#FFFFFF',
  grey: '#9AA0A6',
  lead: '#C9CDD1',
  tick: '#DDE0E3',
  line: 'rgba(255,255,255,.12)',
};

/**
 * Normalizes a licence key to the canonical XXXXX-XXXXX-XXXXX-XXXXX form as the
 * user types or pastes: uppercases, strips anything that isn't A-Z/0-9 (spaces,
 * dashes, line breaks), caps at 20 characters and re-inserts a dash between each
 * group of five. The server applies the same normalization defensively.
 *
 * @param raw The licence key as typed or pasted.
 * @returns The formatted licence key.
 */
export const formatLicenceKey = (raw: string): string => {
  const chars = raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20);
  return chars.replace(/(.{5})(?=.)/g, '$1-');
};

const TickItem = ({ children }: { children: React.ReactNode }) => (
  <Box component="li" sx={{ display: 'flex', gap: '10px', fontSize: '14.5px', color: COLORS.tick, lineHeight: 1.5 }}>
    <Box
      aria-hidden
      sx={{
        flex: '0 0 18px',
        height: '18px',
        mt: '2px',
        borderRadius: '50%',
        background:
          `radial-gradient(circle at center, ${COLORS.yellow} 0 40%, transparent 45%), ` +
          `radial-gradient(circle at center, transparent 0 55%, ${COLORS.yellow} 60% 70%, transparent 75%)`,
      }}
    />
    <span>{children}</span>
  </Box>
);

export const LoginPage = () => {
  const [licenceKey, setLicenceKey] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { createModal } = useContext(ModalContext);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedKey = licenceKey.trim();
    setLoading(true);

    let response;
    try {
      response = await apiAccountLoginPost(trimmedKey, email.trim());
    } finally {
      setLoading(false);
    }

    if (response?.status === 200) {
      return createModal(({ handleRemove }) => {
        return (
          <CloseableModal handleRemove={handleRemove}>
            <DialogTitle>
              Success
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                If the licence key is valid and your email matches the licence owner's domain, a sign-in link has been sent to you.
              </DialogContentText>
            </DialogContent>

          <DialogActions>
            <Button variant="contained" onClick={handleRemove}>Close</Button>
          </DialogActions>
          </CloseableModal>
        );
      });
    }

    return createModal(({ handleRemove }) => {
      return (
        <CloseableModal handleRemove={handleRemove}>
          <DialogTitle>
            Error
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Something went wrong, please try again.
            </DialogContentText>
          </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleRemove}>Close</Button>
        </DialogActions>
        </CloseableModal>
      );
    });
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 2 }}>
        <CircularProgress />
        <Typography variant="h6">Signing you in...</Typography>
      </Container>
    );
  }

  const labelSx = { fontSize: '13px', fontWeight: 500, letterSpacing: '.04em', color: COLORS.lead };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: `
          radial-gradient(900px 600px at 90% -10%, rgba(45,107,98,.45), transparent 60%),
          radial-gradient(700px 500px at -10% 110%, rgba(45,107,98,.45), transparent 60%),
          #1A1A1A`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 3, md: 7 },
          py: 3,
          borderBottom: `1px solid ${COLORS.line}`,
        }}
      >
        <Box component="img" src={logoWhite} alt="DigitalCNC" sx={{ height: 36, display: 'block' }} />
        <MuiLink href="https://digitalcnc.ai" sx={{ fontSize: 14, color: COLORS.grey, textDecoration: 'none', letterSpacing: '.02em' }}>
          {'\u2190 '}digitalcnc.ai
        </MuiLink>
      </Box>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 3, md: 7 }, py: { xs: 4, md: 8 } }}>
        <Box
          sx={{
            width: 'min(960px, 100%)',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            background: 'rgba(255,255,255,.035)',
            border: `1px solid ${COLORS.line}`,
            borderRadius: '14px',
            overflow: 'hidden',
            backdropFilter: 'blur(4px)',
          }}
        >
        {/* Left: brand / value panel */}
        <Box
          component="section"
          sx={{
            p: { xs: '28px', md: 'clamp(28px,4vw,48px)' },
            borderRight: { md: `1px solid ${COLORS.line}` },
            borderBottom: { xs: `1px solid ${COLORS.line}`, md: 'none' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '22px',
          }}
        >
          <Typography component="h1" sx={{ fontWeight: 600, fontSize: 'clamp(24px,2.6vw,32px)', lineHeight: 1.15 }}>
            Stop guessing.<br />
            <Box component="span" sx={{ color: COLORS.yellow }}>Start knowing.</Box>
          </Typography>
          <Typography sx={{ color: COLORS.lead, fontSize: '15px', lineHeight: 1.6 }}>
            Welcome to your DigitalCNC licence portal. Sign in to download the latest software version and start analysing toolpaths against your machine's real behaviour.
          </Typography>
          <Typography sx={{ color: COLORS.lead, fontSize: '15px', lineHeight: 1.6 }}>
            Installers for Siemens NX, CATIA V5 and Mastercam 2026 - installed in under 30 seconds
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', m: 0, p: 0 }}>
            <TickItem>No restricted features, no locked modules</TickItem>
            <TickItem>On-premise. No data leaves your facility</TickItem>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              fontSize: '13px',
              color: COLORS.grey,
              borderTop: `1px solid ${COLORS.line}`,
              pt: '18px',
              lineHeight: 1.4,
            }}
          >
            <Box component="img" src={logoWhite} alt="" sx={{ height: 48, width: 'auto', flex: '0 0 auto' }} />
            <Box component="span">
              <Box component="b" sx={{ display: 'block', color: COLORS.white, fontWeight: 500, fontSize: '13.5px' }}>
                Cyber Essentials Plus certified
              </Box>
              Secure, on-premise installation
            </Box>
          </Box>
        </Box>

        {/* Right: the form */}
        <Box
          component="section"
          sx={{
            p: { xs: '28px', md: 'clamp(28px,4vw,48px)' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '18px',
          }}
        >
          <Typography component="h2" sx={{ fontWeight: 500, fontSize: '20px' }}>
            Sign in
          </Typography>
          <form onSubmit={onSubmit}>
            <Stack spacing={2} direction="column">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <Typography component="label" htmlFor="email" sx={labelSx}>
                  Company Email <Box component="em" sx={{ fontStyle: 'normal', color: COLORS.yellow }}>*</Box>
                </Typography>
                <TextField
                  id="email"
                  name="email"
                  type="email"
                  required
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  autoComplete="email"
                  className="form-control"
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <Typography component="label" htmlFor="licenceKey" sx={labelSx}>
                  Licence Key <Box component="em" sx={{ fontStyle: 'normal', color: COLORS.yellow }}>*</Box>
                </Typography>
                <TextField
                  id="licenceKey"
                  name="licenceKey"
                  required
                  value={licenceKey}
                  onChange={(event) => setLicenceKey(formatLicenceKey(event.target.value))}
                  inputProps={{ autoComplete: 'off', spellCheck: false, style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '.08em' } }}
                  placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
                  className="form-control"
                />
                <Typography sx={{ fontSize: '12.5px', color: COLORS.grey }}>
                  Use the licence key from your welcome email.
                </Typography>
              </Box>
              <Button
                type="submit"
                sx={{
                  backgroundColor: COLORS.yellow,
                  color: '#1A1A1A',
                  fontWeight: 600,
                  fontSize: '15.5px',
                  borderRadius: '8px',
                  padding: '14px 18px',
                  '&:hover': { backgroundColor: COLORS.yellow, filter: 'brightness(1.06)' },
                }}
              >
                Sign in
              </Button>
            </Stack>
          </form>
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
              fontSize: '13px',
              color: COLORS.lead,
              lineHeight: 1.55,
              background: 'rgba(235,227,45,.06)',
              border: '1px solid rgba(235,227,45,.25)',
              borderRadius: '8px',
              p: '12px 14px',
            }}
          >
            <Box component="svg" viewBox="0 0 16 16" fill="none" aria-hidden sx={{ flex: '0 0 16px', width: '16px', height: '16px', mt: '2px' }}>
              <path d="M1 4l7 5 7-5M2 3h12a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V4a1 1 0 011-1z" stroke={COLORS.yellow} strokeWidth="1.3" />
            </Box>
            <span>We'll send a short authorisation email to confirm it's you. It usually arrives within a minute - please check your spam folder.</span>
          </Box>
          <Typography sx={{ fontSize: '13px', color: COLORS.grey }}>
            Trouble signing in?{' '}
            <Box component="a" href="mailto:support@digitalcnc.ai" sx={{ color: COLORS.yellow, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              support@digitalcnc.ai
            </Box>
          </Typography>
        </Box>
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
          <Typography sx={{ fontSize: '12.5px', color: COLORS.grey, lineHeight: 1.5 }}>
            Activating a computer without internet access? Sign in first, then use Offline Activation from the Licences page.
          </Typography>
        </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: `1px solid ${COLORS.line}`,
          px: { xs: 3, md: 7 },
          py: 2.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px 28px',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '12.5px',
          color: COLORS.grey,
        }}
      >
        <span>Copyright © 2026 DigitalCNC | Yorkshire AI Labs</span>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box component="a" href="mailto:support@digitalcnc.ai" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>Support</Box>
          <Box component="a" href="https://digitalcnc.ai/privacy-policy/" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>Privacy Policy</Box>
          <Box component="a" href="https://www.linkedin.com/company/digitalcnc" rel="noopener" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>LinkedIn</Box>
          <Box component="a" href="https://digitalcnc.ai" sx={{ color: COLORS.lead, textDecoration: 'none', '&:hover': { color: COLORS.white } }}>digitalcnc.ai</Box>
        </Box>
      </Box>
    </Box>
  );
}
